import type { Classroom } from "~/types/classroom";

export function usePiniaPersist(): void {
   const authStore = useAuthStore();
   const classStore = useClassesStore();
   const { setStorage, getStorage, clearStorage } = useLocalStorage();

   // Safe JSON parsing helper
   const safeJsonParse = <T>(value: string | null, fallback: T): T => {
      if (!value || value === "undefined" || value === "null") {
         return fallback;
      }
      try {
         return JSON.parse(value) as T;
      } catch (error) {
         console.warn("Failed to parse JSON from localStorage:", error);
         return fallback;
      }
   };

   // Load persisted data ONCE without triggering subscriptions
   const loadAuthData = (): void => {
      const authToken = useCookie("session").value;
      // Extra protection: if no cookie is found, clear local storage
      if (!authToken) {
         clearStorage("auth/user");
         clearStorage("auth/token");
         return;
      }

      const savedUser = getStorage("auth/user");
      const savedAuthed = getStorage("auth/authed");
      const savedToken = getStorage("auth/token");

      if (savedUser && savedAuthed && savedToken) {
         const parsedAuthed = safeJsonParse<boolean>(savedAuthed, false);
         const parsedUser = safeJsonParse<Parameters<typeof authStore.setAuth>[0] | null>(
            savedUser,
            null,
         );
         const parsedToken = safeJsonParse<string | null>(savedToken, null);

         if (parsedAuthed && parsedUser && parsedToken) {
            authStore.setAuth(parsedUser, parsedToken);
         }
      }
   };

   const loadClassData = (): void => {
      const savedAllClasses = getStorage("classes/allClasses");
      const savedActiveClasses = getStorage("classes/activeClasses");
      const savedArchivedClasses = getStorage("classes/archivedClasses");

      if (savedAllClasses) {
         const parsedAllClasses = safeJsonParse<
            Classroom.UserClassesResponse | null
         >(savedAllClasses, null);
         classStore.$state.allClasses = parsedAllClasses;
      }
      if (savedActiveClasses) {
         const parsedActiveClasses = safeJsonParse<Classroom.Simplified[]>(
            savedActiveClasses,
            [],
         );
         classStore.$state.activeClasses = parsedActiveClasses;
      }
      if (savedArchivedClasses) {
         const parsedArchivedClasses = safeJsonParse<Classroom.Simplified[]>(
            savedArchivedClasses,
            [],
         );
         classStore.$state.archivedClasses = parsedArchivedClasses;
      }
   };

   // Load data first
   loadAuthData();
   loadClassData();

   // THEN set up subscriptions for future changes
   authStore.$subscribe(
      (_mutation, state) => {
         try {
            setStorage("auth/user", JSON.stringify(state.user));
            setStorage("auth/authed", JSON.stringify(state.authed));
            setStorage("auth/token", JSON.stringify(state.token));
         } catch (error) {
            console.error("Failed to save auth data to localStorage:", error);
         }
      },
      { detached: true, immediate: true },
   );

   classStore.$subscribe(
      (_mutation, state) => {
         try {
            setStorage(
               "classes/allClasses",
               JSON.stringify(state.allClasses),
            );
            setStorage(
               "classes/activeClasses",
               JSON.stringify(state.activeClasses),
            );
            setStorage(
               "classes/archivedClasses",
               JSON.stringify(state.archivedClasses),
            );
         } catch (error) {
            console.error("Failed to save class data to localStorage:", error);
         }
      },
      { detached: true, immediate: true },
   );
}
